import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { api, buildWebSocketUrl } from "../lib/api";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const [toast, setToast] = useState(null); // Simple toast state

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.read).length);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  };

  useEffect(() => {
    if (!user) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    // Fetch initial notifications
    fetchNotifications();

    // Connect WebSocket
    const wsUrl = buildWebSocketUrl();
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log("WebSocket connected");
      // Ping interval to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === "notification") {
          const newNotif = message.data;
          setNotifications((prev) => [newNotif, ...prev]);
          setUnreadCount((prev) => prev + 1);
          setToast(newNotif);
          setTimeout(() => setToast(null), 5000);
        } else if (message.type === "chat_message") {
          // You could show a toast for messages if you aren't currently in that chat
          // For now, we leave chat updates to the Chat page polling/context, or emit an event
          window.dispatchEvent(new CustomEvent("ws_chat_message", { detail: message.data }));
        }
      } catch (err) {
        console.error("WebSocket message error", err);
      }
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      clearInterval(ws.pingInterval);
    };

    wsRef.current = ws;

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user]);

  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAllAsRead, toast, setToast }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
